import React, { useEffect, useRef, useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Sun, Moon } from 'lucide-react';
import './Layout.css';

export default function Layout() {
  const { user, isAnon, signOut, signInWithGoogle } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const displayName = user?.displayName || user?.email?.split('@')[0] || (isAnon ? 'Guest' : 'User');
  const avatar = user?.photoURL;

  return (
    <div className="layout">
      <nav className="navbar">
        <NavLink to="/dashboard" className="navbar-brand">
          <span className="brand-icon">◆</span>
          <span className="brand-name">Nexel</span>
        </NavLink>

        <div className="navbar-links">
          <NavLink to="/dashboard" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
            Dashboard
          </NavLink>
          {user && !isAnon && (
            <NavLink to="/orgs" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
              Organisations
            </NavLink>
          )}
        </div>

        <div className="navbar-right">
          <button className="theme-toggle" onClick={toggleTheme} title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}>
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          {isAnon && (
            <button className="btn btn-primary" style={{ fontSize: 13 }} onClick={signInWithGoogle}>
              Sign in
            </button>
          )}
          {user && (
            <div className="user-menu" ref={menuRef}>
              <button className="user-btn" onClick={() => setMenuOpen(o => !o)}>
                {avatar
                  ? <img src={avatar} alt={displayName} className="user-avatar" />
                  : <span className="user-initials">{displayName[0].toUpperCase()}</span>
                }
                <span className="user-name">{displayName}</span>
                <span className="chevron">{menuOpen ? '▲' : '▼'}</span>
              </button>
              {menuOpen && (
                <div className="user-dropdown" onClick={() => setMenuOpen(false)}>
                  <NavLink to="/profile" className="dropdown-item">Profile</NavLink>
                  <button className="dropdown-item danger" onClick={handleSignOut}>Sign out</button>
                </div>
              )}
            </div>
          )}
        </div>
      </nav>

      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
}
